import { useCallback, useMemo } from 'react';

import type { DocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import produce from 'immer';
import { maxBy } from 'lodash';

import { readQueryAndThrowMissingField, waitToFinishLoading } from 'common/apollo/helpers';

export type UseStoreHelperOptions<EntityResponse, QueryResponse> = {
  name?: string;
  loading: boolean;
  query: DocumentNode;
  queryVariables?: Record<string, unknown>;
  selectEntities: (resp?: Partial<QueryResponse>) => EntityResponse[];
  selectEntityId: (entity: EntityResponse) => string;
  selectSyncOn: (entity: EntityResponse) => Date;
};

type MutateProducer<QueryResponse> = (q: QueryResponse) => void;
type MutateProducerGetter<QueryResponse, EntityResponse> = (entity: EntityResponse) => MutateProducer<QueryResponse>;

export default function useStoreHelper<EntityResponse, QueryResponse>(
  opts: UseStoreHelperOptions<EntityResponse, QueryResponse>
) {
  const { query, queryVariables, loading, selectEntities, selectEntityId, selectSyncOn } = opts;
  const client = useApolloClient();

  const mutateStore = useCallback(
    (getMutateProducer: MutateProducerGetter<QueryResponse, EntityResponse>) => async (entity: EntityResponse) => {
      try {
        if (loading) {
          await waitToFinishLoading(client, query, queryVariables);
        }

        const prevState = readQueryAndThrowMissingField<QueryResponse>(client.cache, {
          query,
          variables: queryVariables,
        });

        const newState = produce(prevState, getMutateProducer(entity));

        client.writeQuery({
          query,
          variables: queryVariables,
          data: newState,
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [client, loading, query, queryVariables]
  );

  const getWriteProducer: MutateProducerGetter<QueryResponse, EntityResponse> = useCallback(
    (entityToWrite: EntityResponse) => (q: QueryResponse) => {
      const entities = selectEntities(q);

      const newEntityIndex = entities.findIndex(e => selectEntityId!(e) === selectEntityId!(entityToWrite));

      if (newEntityIndex !== -1) {
        entities[newEntityIndex] = maxBy([entityToWrite, entities[newEntityIndex]], selectSyncOn)!;
      } else {
        entities.unshift(entityToWrite);
      }
    },
    [selectEntities, selectEntityId, selectSyncOn]
  );

  const getDeleteProducer: MutateProducerGetter<QueryResponse, EntityResponse> = useCallback(
    (entityToDelete: EntityResponse) => (q: QueryResponse) => {
      const entities = selectEntities(q);

      const deletedEntityIndex = entities.findIndex(e => selectEntityId(e) === selectEntityId(entityToDelete));

      if (deletedEntityIndex !== -1) {
        entities.splice(deletedEntityIndex, 1);
      }
    },
    [selectEntities, selectEntityId]
  );

  const writeToStore = useMemo(() => mutateStore(getWriteProducer), [getWriteProducer, mutateStore]);
  const deleteFromStore = useMemo(() => mutateStore(getDeleteProducer), [getDeleteProducer, mutateStore]);

  return useMemo(
    () => ({
      writeToStore,
      deleteFromStore,
    }),
    [deleteFromStore, writeToStore]
  );
}
